import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { storesApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import type { CreateStoreRequest, UpdateStoreRequest, PaginationParams } from '@geely/shared';

interface UseStoresOptions {
  enabled?: boolean;
}

export function useStores(options: UseStoresOptions = {}) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: queryKeys.stores,
    queryFn: async () => {
      const response = await storesApi.list();
      return response.data;
    },
    enabled,
  });
}

export function useStoresPaginated(params: Required<PaginationParams>) {
  return useQuery({
    queryKey: queryKeys.storesPaginated(params),
    queryFn: async () => {
      const response = await storesApi.listPaginated(params);
      return response.data;
    },
    placeholderData: keepPreviousData,
  });
}

export function useStore(id: string) {
  return useQuery({
    queryKey: queryKeys.store(id),
    queryFn: async () => {
      const response = await storesApi.getById(id);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateStore() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateStoreRequest) => {
      const response = await storesApi.create(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stores });
    },
  });
}

export function useUpdateStore() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateStoreRequest }) => {
      const response = await storesApi.update(id, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stores });
    },
  });
}

export function useDeleteStore() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await storesApi.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stores });
    },
  });
}
